import { useEffect, MouseEvent, useState } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { useForm } from 'react-hook-form';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  getMemberAction,
  selectGetMemberResult,
  UpdateMemberActionResponse,
  selectUpdateMemberResult,
  updateMemberAction,
  member_field_options,
} from './memberSlice';
import { Member } from '../../types';

type Props = {
  tenant_id: string;
  user_id: string;
  successCallback: (result: UpdateMemberActionResponse) => void;
  dismissCallback: (error?: SerializedError) => void;
};

type MemberForm = Required<Omit<Member, 'id'|'name'|'tenant_id'|'owned_by_tenant'|'email'|'email_verified'|'keys'>> & {
  member_name: string;
  password: string;
  password_options: 'not_change'|'empty'|'custom';
};

const initialState: MemberForm = {
  member_name: '',
  username: '',
  password: '',
  state: 'enabled',
  description: '',
  password_options: 'not_change',
};

export function MemberEditForm({ tenant_id, user_id, successCallback, dismissCallback }: Props) {
  const dispatch = useAppDispatch();
  const get_result = useAppSelector(selectGetMemberResult);
  const update_result = useAppSelector(selectUpdateMemberResult);
  const [api_error, setApiError] = useState<SerializedError|undefined>();
  const [is_owned_by_tenant, setIsOwnedByTenant] = useState(false);
  const { handleSubmit, register, watch, reset, formState: { errors } } = useForm({
    defaultValues: initialState,
  });
  const password_options = watch('password_options');
  const show_password_input = is_owned_by_tenant && password_options === 'custom';

  useEffect(() => {
    dispatch(getMemberAction({ tenant_id, user_id }));
  }, [tenant_id, user_id, dispatch]);

  useEffect(() => {
    if (get_result) {
      if (getMemberAction.fulfilled.match(get_result)) {
        reset({
          ...initialState,
          ...get_result.payload,
          password_options: 'not_change',
          member_name: get_result.payload.name,
        });
        setIsOwnedByTenant(get_result.payload.owned_by_tenant ?? false);
      }
      if (getMemberAction.rejected.match(get_result)) {
        const error = get_result.error;
        setApiError(error);
        dismissCallback(error);
      }
    }
  }, [get_result, reset, dismissCallback]);

  useEffect(() => {
    if (update_result) {
      if (updateMemberAction.fulfilled.match(update_result)) {
        successCallback(update_result.payload);
      } else {
        setApiError(update_result.error);
        const subscription = watch(() => {
          setApiError(undefined);
          subscription.unsubscribe();
        });
      }
    }
  }, [update_result, successCallback, watch]);

  const handleUpdate = async(data: MemberForm) => {
    let password;
    if (data.username) {
      if (data.password_options === 'empty') {
        password = null;
      } else if (data.password_options === 'custom') {
        password = data.password;
      }
    }
    let result = await dispatch(updateMemberAction({
      tenant_id,
      user_id,
      body: {
        ...data,
        name: data.member_name,
        password,
      },
    }));
    if (updateMemberAction.fulfilled.match(result)) {
      successCallback(result.payload);
    } else {
      setApiError(result.error);
      const subscription = watch(() => {
        setApiError(undefined);
        subscription.unsubscribe();
      });
    }
  };
  const handleCancel = (event: MouseEvent) => {
    event.preventDefault();
    dismissCallback();
  };
  return (
    <form onSubmit={handleSubmit(handleUpdate)}>
      <fieldset>
        <div className="mb-3 row">
          <label htmlFor="member_member_id" className="col-sm-3 col-form-label">UserId</label>
          <div className="col-sm-6">
            <input disabled type="text" id="member_member_id" className="form-control-plaintext"
              value={user_id}
            />
          </div>
        </div>
        <div className="mb-3 row">
          <label htmlFor="member_name" className="col-sm-3 col-form-label">Name</label>
          <div className="col-sm-6">
            <input disabled={!is_owned_by_tenant} type="text" id="member_name" className="form-control"
              { ...register('member_name', member_field_options.name) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.member_name?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="username" className="col-sm-3 col-form-label">Username</label>
          <div className="col-sm-6">
            <input disabled={!is_owned_by_tenant} type="text" id="username" className="form-control"
            { ...register('username', {
              ...member_field_options.username,
              required: false
            }) }
            />
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.username?.message}</span>
        </div>
        {is_owned_by_tenant &&
        <div className="mb-3 row">
          <label className="col-sm-3 col-form-label">Password</label>
          <div className="mt-2 col-sm-6">
            <div className="form-check form-check-inline">
              <input disabled={!is_owned_by_tenant} className="form-check-input" type="radio"
                id="not_change_password" value="not_change"
                { ...register('password_options', { required: true }) }
                />
              <label className="form-check-label" htmlFor="not_change_password">
                Not Change
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input className="form-check-input" type="radio"
                id="empty_password" value="empty"
                { ...register('password_options', { required: true }) }
              />
              <label className="form-check-label" htmlFor="empty_password">
                Empty
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input className="form-check-input" type="radio"
                id="custom_password" value="custom"
                { ...register('password_options', { required: true }) }
              />
              <label className="form-check-label" htmlFor="custom_password">
                Custom
              </label>
            </div>
            {show_password_input && <>
            <div className="mt-2">
              <input type="password" id="password" className="form-control" autoComplete="new-password"
                { ...register('password', member_field_options.password) }
              />
            </div>
            <span className="col-sm-3 text-danger py-1">{errors.password?.message}</span>
            </>}
          </div>
        </div>
        }
        <div className="mb-3 row">
          <label htmlFor="member_description" className="col-sm-3 col-form-label">Description</label>
          <div className="col-sm-6">
            <textarea id="member_description" className="form-control"
              { ...register('description', member_field_options.description) }
            >
            </textarea>
          </div>
          <span className="col-sm-3 text-danger py-1">{errors.description?.message}</span>
        </div>
        <div className="mb-3 row">
          <label htmlFor="state" className="col-sm-3 col-form-label">State</label>
          <div className="col-sm-6">
            <select id="state" className="form-select"
              { ...register('state') }
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>
        <div className="d-flex">
          <span>{api_error?.message ?? api_error?.name}</span>
        </div>
        <div className="d-flex my-3 justify-content-center">
          <button type="submit" className="btn btn-primary mx-3">Update</button>
          <button className="btn btn-light mx-1" onClick={(e) => handleCancel(e)}>Cancel</button>
        </div>
      </fieldset>
    </form>
  );
}
